import { useState, type ImgHTMLAttributes } from "react";
import { transformedImageUrl, imageSrcSet, type ImgTransform } from "@/lib/image-url";

type SmartImgProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> & {
  /** Original Supabase Storage URL (unsigned, /object/public/...). */
  source: string | null | undefined;
  /** Transform options for the default (1x) src. */
  transform?: ImgTransform;
  /** Optional responsive widths for srcSet. */
  srcSetWidths?: number[];
};

/**
 * Image that requests a transformed (smaller) variant from Supabase Storage
 * but transparently falls back to the original URL when the transform service
 * rejects the source (e.g. file too large / dimensions out of bounds).
 */
export function SmartImg({ source, transform, srcSetWidths, ...rest }: SmartImgProps) {
  const [failed, setFailed] = useState(false);
  if (!source) return <img {...rest} />;
  if (failed) {
    return <img {...rest} src={source} />;
  }
  return (
    <img
      {...rest}
      src={transformedImageUrl(source, transform)}
      srcSet={srcSetWidths ? imageSrcSet(source, srcSetWidths, transform) : undefined}
      onError={() => setFailed(true)}
    />
  );
}